import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import { TextField } from "atoms/text-field/TextField";

import OnboardingHeaderV3 from "../../components/OnboardingScreenHeader/OnboardingHeaderV3";

type TranslateFn = ReturnType<typeof useTranslation>["t"];

// Zod schema for name validation. Built from a translator so validation copy is
// localized; the shape is stable regardless of language.
const makeNameFormSchema = (t: TranslateFn) =>
  z.object({
    firstName: z
      .string()
      .min(1, t("nameForm.firstNameRequired"))
      .min(2, t("nameForm.firstNameMin"))
      .max(50, t("nameForm.firstNameMax"))
      .regex(/^[a-zA-Z\s\-']+$/, t("nameForm.firstNamePattern")),
    lastName: z
      .string()
      .min(1, t("nameForm.lastNameRequired"))
      .min(2, t("nameForm.lastNameMin"))
      .max(50, t("nameForm.lastNameMax"))
      .regex(/^[a-zA-Z\s\-']+$/, t("nameForm.lastNamePattern")),
  });

export type NameFormData = z.infer<ReturnType<typeof makeNameFormSchema>>;

export interface NameFormProps {
  initialFirstName?: string;
  initialLastName?: string;
  onSubmit?: (data: NameFormData) => void | Promise<void>;
  className?: string;
  submitButtonText?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
}

export const NameForm = ({
  initialFirstName = "",
  initialLastName = "",
  onSubmit,
  className = "",
  submitButtonText,
  prefix,
  suffix,
}: NameFormProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const nameFormSchema = useMemo(() => makeNameFormSchema(t), [t]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<NameFormData>({
    resolver: zodResolver(nameFormSchema),
    defaultValues: {
      firstName: initialFirstName,
      lastName: initialLastName,
    },
    // only show errors after submit, so we dont see red when typing our name
    mode: "onSubmit",
  });

  const onSubmitForm = async (data: NameFormData) => {
    if (!onSubmit) return;

    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await onSubmit(data);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t("nameForm.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && isValid && !isSubmitting) {
      event.preventDefault();
      handleSubmit(onSubmitForm)();
    }
  };

  return (
    <div className={`flex w-full max-w-sm flex-col gap-9 ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? <div className="flex w-full items-center justify-center">{prefix}</div> : null}

      {/* Header */}
      <OnboardingHeaderV3 title={t("nameForm.title")} align="left" />

      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-3">
        {/* First Name Field */}
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              type="text"
              label={t("nameForm.firstNameLabel")}
              placeholder={t("nameForm.firstNameLabel")}
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.firstName}
              supportingText={errors.firstName?.message}
              disabled={isSubmitting}
              onKeyDown={handleKeyDown}
              labelClassName="hidden"
              className="!ring-0 focus:!ring-0"
            />
          )}
        />

        {/* Last Name Field */}
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              type="text"
              label={t("nameForm.lastNameLabel")}
              placeholder={t("nameForm.lastNameLabel")}
              name="lastName"
              autoComplete="family-name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.lastName}
              supportingText={errors.lastName?.message}
              disabled={isSubmitting}
              onKeyDown={handleKeyDown}
              labelClassName="hidden"
              className="!ring-0 focus:!ring-0"
            />
          )}
        />

        {/* Submit Button with Error */}
        <div className="mt-6 flex flex-col gap-2">
          {submitError && <div className="text-center text-sm text-red">{submitError}</div>}
          <Button submit fullWidth disabled={!isValid || isSubmitting}>
            {isSubmitting ? t("nameForm.saving") : (submitButtonText ?? t("common.continue"))}
          </Button>
        </div>
      </form>

      {/* Optional suffix slot for extra content */}
      {suffix ? <div className="mt-6 w-full">{suffix}</div> : null}
    </div>
  );
};
